import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Alert,
    useTheme as useMuiTheme,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend as RechartsLegend,
} from 'recharts';
import { useTheme } from '../contexts/ThemeContext';
import {
    getTableHeaderStyles,
    getTableContainerStyles,
    getTableRowStyles,
    getTableCellStyles,
} from '../styles/tableStyles';
import { StatMiniCard, DashboardCard } from './ChannelDashboard';
// W-6: Shared utilities — no duplicate implementations across the two dashboards.
import { downloadCsv, MultiSelectFilter, ConfirmActionDialog } from '../utils/dashboardShared';
import {
    getAclFilters,
    getAclRecords,
    postAclAction,
    AclDashboardRecord,
    AclFilterOptions,
    AclQuarterSummary,
    AclSummaryCounts,
    AclApprovalWorkflow,
} from '../services/shayDashboardService';

const STATUS_LABEL: Record<string, string> = { true: 'Pending', false: 'Revoked' };
// W-2: Explicit fallback color for any status value not in the map.
const ROW_COLOR: Record<string, string> = {
    true: 'rgba(255, 243, 205, 0.35)',
    false: 'rgba(248, 215, 218, 0.35)',
};
const UNKNOWN_ROW_COLOR = 'rgba(200, 200, 200, 0.15)';

const labelForStatus = (status?: string | null) => {
    const key = (status || '').toLowerCase();
    return STATUS_LABEL[key] || status || '(unknown)';
};

// Privileged/non-privileged classification — the backend now sends `is_privileged` directly
// (confirmed 2026-08-10 /acl response). Previously derived via an "adm" username-prefix
// heuristic before this field existed; that guess is retired now the real flag is live.
const isPrivileged = (r: AclDashboardRecord) => Boolean(r.is_privileged);

/** Matches the mockup's "<p class='hint'>" — a one-line description under each panel title.
 * Note: unlike Trusted Advisor's still-pending fields, every ACL field these hints describe
 * (is_privileged, group_name, role, access_end, approval_status) is now confirmed live in the
 * real API — so no "New field" badge is applied here, to avoid implying the data isn't real. */
const PanelHint = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5, mt: -1 }}>{children}</Typography>
);

const EMPTY_FILTER_OPTIONS: AclFilterOptions = {
    report_names: [],
    user_types: [],
    access_statuses: [],
    manager_decisions: [],
};

interface AclAccessControlDashboardProps {
    /** B-2: Forwarded to the API so the backend can scope results to this channel. */
    channelId?: string;
}

const AclAccessControlDashboard: React.FC<AclAccessControlDashboardProps> = ({ channelId }) => {
    const { isDarkMode } = useTheme();
    const muiTheme = useMuiTheme();
    const palette = muiTheme.palette;

    const [records, setRecords] = useState<AclDashboardRecord[]>([]);
    const [quarterOverQuarter, setQuarterOverQuarter] = useState<AclQuarterSummary[]>([]);
    const [summaryCounts, setSummaryCounts] = useState<AclSummaryCounts>({});
    const [approvalWorkflowApi, setApprovalWorkflowApi] = useState<AclApprovalWorkflow>({});
    const [filterOptions, setFilterOptions] = useState<AclFilterOptions>(EMPTY_FILTER_OPTIONS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [reportFilter, setReportFilter] = useState<string[]>([]);
    const [userTypeFilter, setUserTypeFilter] = useState<string[]>([]);
    const [accessStatusFilter, setAccessStatusFilter] = useState<string[]>([]);
    const [managerDecisionFilter, setManagerDecisionFilter] = useState<string[]>([]);
    const [revFrom, setRevFrom] = useState('');
    const [revTo, setRevTo] = useState('');
    const [revokeLoading, setRevokeLoading] = useState<Record<string, boolean>>({});
    const [revokeDone, setRevokeDone] = useState<Record<string, boolean>>({});
    const [revokeError, setRevokeError] = useState<Record<string, string>>({});
    const [retainLoading, setRetainLoading] = useState<Record<string, boolean>>({});
    const [retainDone, setRetainDone] = useState<Record<string, boolean>>({});
    const [retainError, setRetainError] = useState<Record<string, string>>({});
    const [revokeConfirmTarget, setRevokeConfirmTarget] = useState<{ r: AclDashboardRecord; key: string } | null>(null);

    useEffect(() => {
        // W-1 + W-5: AbortController cancels in-flight fetches on unmount / rapid remount,
        // preventing both stale state updates and piled-up abandoned network requests.
        const controller = new AbortController();
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const [filters, aclData] = await Promise.all([
                    getAclFilters(controller.signal),
                    // B-2: pass channelId so the backend can scope results server-side.
                    getAclRecords({}, channelId, controller.signal),
                ]);
                setFilterOptions(filters);
                setRecords(aclData.records);
                setQuarterOverQuarter(aclData.quarterOverQuarter);
                setSummaryCounts(aclData.summaryCounts);
                setApprovalWorkflowApi(aclData.approvalWorkflow);
            } catch (err: unknown) { // M-2: unknown instead of any
                // If the request was aborted (e.g. component unmounted or React StrictMode
                // double-invocation in dev), skip all state updates — the component is
                // either gone or about to re-run the effect cleanly.
                if (controller.signal.aborted) return;
                console.error('Failed to load ACL dashboard data', err);
                // W-3: Distinguish auth failure from a transient server error.
                const msg = err instanceof Error ? err.message : '';
                if (msg === 'HTTP_401' || msg === 'HTTP_403') {
                    setError('You are not authorised to view ACL Access Control data.');
                } else {
                    setError('Failed to load ACL Access Control data. Please try again.');
                }
            } finally {
                // Guard: `finally` runs even when `catch` returns, so only update
                // loading state if this effect instance was not aborted.
                if (!controller.signal.aborted) setLoading(false);
            }
        };
        load();
        return () => controller.abort();
    }, [channelId]);

    // Filter option lists come from /acl/filters; fall back to values seen in the fetched
    // records in case the filters endpoint omits a field the records actually contain.
    const reportOptions = useMemo(() => {
        const fromRecords = [...new Set(records.map(r => r.report_name).filter((v): v is string => !!v))];
        return [...new Set([...filterOptions.report_names, ...fromRecords])].sort();
    }, [filterOptions, records]);
    const userTypeOptions = useMemo(() => {
        const fromRecords = [...new Set(records.map(r => r.user_type).filter((v): v is string => !!v))];
        return [...new Set([...filterOptions.user_types, ...fromRecords])].sort();
    }, [filterOptions, records]);
    const accessStatusOptions = useMemo(() => {
        const fromRecords = [...new Set(records.map(r => r.access_status).filter((v): v is string => !!v))];
        return [...new Set([...filterOptions.access_statuses, ...fromRecords])].sort();
    }, [filterOptions, records]);
    const managerDecisionOptions = useMemo(() => {
        const fromRecords = [...new Set(records.map(r => r.manager_decision).filter((v): v is string => !!v))];
        return [...new Set([...filterOptions.manager_decisions, ...fromRecords])].sort();
    }, [filterOptions, records]);

    const filtered = useMemo(() => {
        return records.filter((r) => {
            if (reportFilter.length && !(r.report_name && reportFilter.includes(r.report_name))) return false;
            if (userTypeFilter.length && !(r.user_type && userTypeFilter.includes(r.user_type))) return false;
            if (accessStatusFilter.length && !(r.access_status && accessStatusFilter.includes(r.access_status))) return false;
            if (managerDecisionFilter.length && !(r.manager_decision && managerDecisionFilter.includes(r.manager_decision))) return false;
            if (revFrom && (!r.revocation_date || r.revocation_date < revFrom)) return false;
            if (revTo && (!r.revocation_date || r.revocation_date > revTo)) return false;
            return true;
        });
    }, [records, reportFilter, userTypeFilter, accessStatusFilter, managerDecisionFilter, revFrom, revTo]);

    const totalRecords = filtered.length;
    const uniqueUsers = new Set(filtered.map(r => r.username)).size;
    const revoked = filtered.filter(r => (r.access_status || '').toLowerCase() === 'false').length;
    const privilegedCount = filtered.filter(isPrivileged).length;

    // Drives the "Expired Access" summary card (ref: tvs_aiops_dashboards.html mockup).
    const expiredCount = filtered.filter(r => {
        if (!r.access_end) return false;
        const daysUntil = (new Date(r.access_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
        return !Number.isNaN(daysUntil) && daysUntil < 0;
    }).length;
    const expiringSoonCount = filtered.filter(r => {
        if (!r.access_end) return false;
        const daysUntil = (new Date(r.access_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
        return !Number.isNaN(daysUntil) && daysUntil >= 0 && daysUntil <= 30;
    }).length;

    // Drives the "Awaiting Approval" summary card. "Awaiting" here means the workflow hasn't
    // reached a terminal 'approved' state yet — mirrors the mockup's own framing (awaiting +
    // reminder-loop are both still-open approval states).

    const byPrivilege = useMemo(() => {
        const privileged = filtered.filter(isPrivileged).length;
        const nonPrivileged = filtered.length - privileged;
        return [
            { name: 'Privileged', value: privileged },
            { name: 'Non-privileged', value: nonPrivileged },
        ];
    }, [filtered]);

    const byAccessStatus = useMemo(() => {
        const counts: Record<string, number> = {};
        filtered.forEach(r => {
            const label = labelForStatus(r.access_status);
            counts[label] = (counts[label] || 0) + 1;
        });
        return Object.entries(counts).map(([name, value]) => ({ name, value }));
    }, [filtered]);

    // Only counts privileged users, matching the mockup's own "Privileged Users by Group"
    // scope. `group_name` is live but null on every currently-observed privileged record —
    // shows as "(not set)" until the backend starts populating it for privileged users.
    const byGroup = useMemo(() => {
        const counts: Record<string, number> = {};
        filtered.forEach(r => {
            if (!isPrivileged(r)) return;
            const key = r.group_name || '(not set)';
            counts[key] = (counts[key] || 0) + 1;
        });
        return Object.entries(counts)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count);
    }, [filtered]);

    // Only counts non-privileged users, matching the mockup's own "Non-Privileged Users by
    // Role" scope. `role` is live — real values ("Standard User") now appear here.
    const byRole = useMemo(() => {
        const counts: Record<string, number> = {};
        filtered.forEach(r => {
            if (isPrivileged(r)) return;
            const key = r.role || '(not set)';
            counts[key] = (counts[key] || 0) + 1;
        });
        return Object.entries(counts)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count);
    }, [filtered]);

    // `access_end` is live — deliberately distinct from `revocation_date`: revocation_date is
    // a past event (access already pulled), access_end is a forward-looking access-period expiry.
    const accessExpiryBuckets = useMemo(() => {
        const buckets = { 'Expired': 0, '<30d': 0, '30–90d': 0, 'No expiry set': 0 };
        const now = Date.now();
        filtered.forEach(r => {
            if (!r.access_end) { buckets['No expiry set']++; return; }
            const daysUntil = (new Date(r.access_end).getTime() - now) / (1000 * 60 * 60 * 24);
            if (Number.isNaN(daysUntil)) { buckets['No expiry set']++; return; }
            if (daysUntil < 0) buckets['Expired']++;
            else if (daysUntil <= 30) buckets['<30d']++;
            else if (daysUntil <= 90) buckets['30–90d']++;
            else buckets['No expiry set']++;
        });
        return Object.entries(buckets).map(([name, count]) => ({ name, count }));
    }, [filtered]);
    // Card update: only "Expired" and "No expiry set" are shown — "<30d"/"30–90d" bars removed.
    const visibleExpiryBuckets = useMemo(
        () => accessExpiryBuckets.filter(b => b.name === 'Expired' || b.name === 'No expiry set'),
        [accessExpiryBuckets]
    );

    // Card update: per-record `approval_status` is null on almost every observed record, so
    // the chart is now driven directly by the backend's pre-aggregated `summary.approval_workflow`
    // stage totals instead of being derived (and always empty) client-side.
    const approvalWorkflowChart = useMemo(() => ([
        { name: 'Notified', count: approvalWorkflowApi.notified ?? 0 },
        { name: 'Approved', count: approvalWorkflowApi.approved ?? 0 },
        { name: 'Not yet revoked', count: approvalWorkflowApi.not_yet_revoked ?? 0 },
        { name: 'Reminders', count: approvalWorkflowApi.reminders ?? 0 },
    ]), [approvalWorkflowApi]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert>;
    }

    const requestRevokeAccess = (r: AclDashboardRecord, key: string) => {
        setRevokeConfirmTarget({ r, key });
    };

    const handleRevokeAccess = async () => {
        if (!revokeConfirmTarget) return;
        const { r, key } = revokeConfirmTarget;
        setRevokeConfirmTarget(null);
        setRevokeLoading(prev => ({ ...prev, [key]: true }));
        setRevokeError(prev => { const n = { ...prev }; delete n[key]; return n; });
        try {
            await postAclAction({
                request_type: 'revoke_access',
                report_name: r.report_name ?? '',
                username: r.username ?? '',
                instance_id: r.instance_id ?? '',
            });
            setRevokeDone(prev => ({ ...prev, [key]: true }));
            // Update the record's access_status in local state so the button stays disabled
            // across filter changes and re-renders (revokeDone key contains idx which shifts).
            setRecords(prev => prev.map(rec => rec === r ? { ...rec, access_status: 'false' } : rec));
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to revoke access';
            setRevokeError(prev => ({ ...prev, [key]: msg }));
        } finally {
            setRevokeLoading(prev => { const n = { ...prev }; delete n[key]; return n; });
        }
    };

    const handleRetainAccess = async (r: AclDashboardRecord, key: string) => {
        setRetainLoading(prev => ({ ...prev, [key]: true }));
        setRetainError(prev => { const n = { ...prev }; delete n[key]; return n; });
        try {
            await postAclAction({
                request_type: 'retain_access',
                report_name: r.report_name ?? '',
                username: r.username ?? '',
                instance_id: r.instance_id ?? '',
            });
            setRetainDone(prev => ({ ...prev, [key]: true }));
            // Mirror access_status update: mark manager_decision so the button stays
            // disabled across re-renders without relying on the idx-based retainDone key.
            setRecords(prev => prev.map(rec => rec === r ? { ...rec, manager_decision: 'Approved' } : rec));
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to retain access';
            setRetainError(prev => ({ ...prev, [key]: msg }));
        } finally {
            setRetainLoading(prev => { const n = { ...prev }; delete n[key]; return n; });
        }
    };

    return (
        <Box>
            <Accordion sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: '12px !important', boxShadow: 'none', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Additional Filters</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr 1fr' }, gap: 2 }}>
                        <MultiSelectFilter label="Report" options={reportOptions} value={reportFilter} onChange={setReportFilter} />
                        <MultiSelectFilter label="User Type" options={userTypeOptions} value={userTypeFilter} onChange={setUserTypeFilter} />
                        <MultiSelectFilter label="Access Status" options={accessStatusOptions} value={accessStatusFilter} onChange={setAccessStatusFilter} />
                        <MultiSelectFilter label="Manager Decision" options={managerDecisionOptions} value={managerDecisionFilter} onChange={setManagerDecisionFilter} />
                        <TextField label="Revocation From" type="date" size="small" value={revFrom} onChange={(e) => setRevFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                        <TextField label="Revocation To" type="date" size="small" value={revTo} onChange={(e) => setRevTo(e.target.value)} InputLabelProps={{ shrink: true }} />
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, letterSpacing: '0.05em' }}>Summary</Typography>
            {/* 5 summary cards shown — "Privileged Users" hidden below (Hide_cards branch).
                gridTemplateColumns updated from repeat(6, 1fr) to repeat(5, 1fr) to match, so
                the row fills evenly instead of leaving a trailing empty column. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
                <StatMiniCard
                    title="Total Records"
                    content={
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '2.125rem', color: 'text.primary' }}>{totalRecords}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                {summaryCounts.instances ?? 0} servers scanned
                            </Typography>
                        </Box>
                    }
                />
                <StatMiniCard title="Unique Users" content={<Box><Typography variant="h6" sx={{ fontWeight: 700, fontSize: '2.125rem', color: 'text.primary' }}>{uniqueUsers}</Typography></Box>} />
                {/* Hidden (Hide_cards branch) — accent-red per mockup, matches "Privileged = red"
                    used in the pie/bar charts below. gridTemplateColumns above reduced from 6 to
                    5 to match this card being hidden.
                <StatMiniCard title="Privileged Users" content={<Box><Typography variant="h6" sx={{ fontWeight: 700, fontSize: '2.125rem', color: 'error.main' }}>{privilegedCount}</Typography></Box>} />
                */}
                <StatMiniCard
                    title="Expired Access"
                    content={
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.875rem', color: 'warning.main' }}>{expiredCount}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                {expiringSoonCount} expiring &lt; 30d
                            </Typography>
                        </Box>
                    }
                />
                <StatMiniCard
                    title="Awaiting Approval"
                    content={
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.875rem', color: 'text.primary' }}>{summaryCounts.not_yet_revoked ?? 0}</Typography>
                        </Box>
                    }
                />
                {/* accent-green per mockup, plus auto/manual sub-breakdown from the live revocation_method field */}
                <StatMiniCard
                    title="Revoked"
                    content={
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.875rem', color: 'success.main' }}>{revoked}</Typography>
                        </Box>
                    }
                />
            </Box>

            {totalRecords === 0 ? (
                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
                    <Typography color="text.secondary">No ACL records match the selected filters.</Typography>
                </Paper>
            ) : (
                <>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, letterSpacing: '0.05em' }}>Breakdown</Typography>
                    {/* 4 of the mockup's 6 breakdown cards shown — "Privileged vs Non-Privileged"
                        and "Privileged Users by Group" hidden below (Hide_cards branch). Grid
                        changed from 3 columns to 2: with exactly 4 cards remaining, 2 columns
                        gives two full, evenly-balanced rows (By Access Status + Non-Privileged
                        Users by Role, then Access Expiry Buckets + Approval Workflow) instead of
                        a lopsided single card stranded alone on row 2's left edge under 3 columns. */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, mb: 3 }}>
                        {/* Hidden (Hide_cards branch)
                        <DashboardCard title="Privileged vs Non-Privileged">
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie data={byPrivilege} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={44} outerRadius={80}>
                                        {byPrivilege.map((entry) => (
                                            <Cell
                                                key={entry.name}
                                                fill={entry.name === 'Privileged' ? palette.error.main : palette.primary.main}
                                                stroke={palette.background.paper}
                                                strokeWidth={2}
                                            />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip
                                        contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 8, boxShadow: muiTheme.shadows[8] }}
                                        itemStyle={{ color: palette.text.primary }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 1, pb: 1 }}>
                                {byPrivilege.map((entry) => {
                                    const color = entry.name === 'Privileged' ? palette.error.main : palette.primary.main;
                                    return (
                                        <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                {entry.name}: <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{entry.value}</Box>
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </DashboardCard>

                        <DashboardCard title="Privileged Users by Group">
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={byGroup} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={palette.divider} horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 11, fill: palette.text.secondary }} allowDecimals={false} axisLine={false} tickLine={false} />
                                    <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11, fill: palette.text.primary }} axisLine={false} tickLine={false} />
                                    <RechartsTooltip
                                        cursor={{ fill: palette.action.hover }}
                                        contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 8, boxShadow: muiTheme.shadows[8] }}
                                        itemStyle={{ color: palette.text.primary }}
                                        labelStyle={{ color: palette.text.secondary }}
                                    />
                                    <Bar dataKey="count" fill={palette.error.main} radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </DashboardCard>
                        */}

                        <DashboardCard title="By Access Status">
                            <PanelHint>Current vs revoked</PanelHint>
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie data={byAccessStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={44} outerRadius={80}>
                                        {byAccessStatus.map((entry) => (
                                            <Cell
                                                key={entry.name}
                                                fill={entry.name === 'Pending' ? palette.warning.main : entry.name === 'Revoked' ? palette.error.main : palette.grey[400]}
                                                stroke={palette.background.paper}
                                                strokeWidth={2}
                                            />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip
                                        contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 8, boxShadow: muiTheme.shadows[8] }}
                                        itemStyle={{ color: palette.text.primary }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            {/* Legend outside SVG so long labels never clip against the card edge */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 1, pb: 1 }}>
                                {byAccessStatus.map((entry) => {
                                    const color = entry.name === 'Pending' ? palette.warning.main
                                        : entry.name === 'Revoked' ? palette.error.main
                                        : palette.grey[400];
                                    return (
                                        <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                {entry.name}: <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{entry.value}</Box>
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </DashboardCard>

                        <DashboardCard title="Non-Privileged Users by Role">
                            <PanelHint>Role mapping, via the live role field</PanelHint>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={byRole} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={palette.divider} horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 11, fill: palette.text.secondary }} allowDecimals={false} axisLine={false} tickLine={false} />
                                    <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11, fill: palette.text.primary }} axisLine={false} tickLine={false} />
                                    <RechartsTooltip
                                        cursor={{ fill: palette.action.hover }}
                                        contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 8, boxShadow: muiTheme.shadows[8] }}
                                        itemStyle={{ color: palette.text.primary }}
                                        labelStyle={{ color: palette.text.secondary }}
                                    />
                                    <Bar dataKey="count" fill={palette.primary.main} radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </DashboardCard>

                        <DashboardCard title="Access Expiry Buckets">
                            <PanelHint>Access period tracking, via the live access_end field</PanelHint>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={visibleExpiryBuckets} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={palette.divider} />
                                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: palette.text.secondary }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: palette.text.secondary }} allowDecimals={false} axisLine={false} tickLine={false} />
                                    <RechartsTooltip
                                        cursor={{ fill: palette.action.hover }}
                                        contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 8, boxShadow: muiTheme.shadows[8] }}
                                        itemStyle={{ color: palette.text.primary }}
                                    />
                                    {/* Per-bucket severity coloring: Expired=red, No expiry set=gray (unknown) */}
                                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                        {visibleExpiryBuckets.map((entry) => (
                                            <Cell key={entry.name} fill={
                                                entry.name === 'Expired' ? palette.error.main
                                                    : palette.grey[400]
                                            } />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </DashboardCard>

                        <DashboardCard title="Approval Workflow">
                            <PanelHint>Notify, approve, remind — via the live approval_status field</PanelHint>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={approvalWorkflowChart} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={palette.divider} />
                                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: palette.text.secondary }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={50} />
                                    <YAxis tick={{ fontSize: 11, fill: palette.text.secondary }} allowDecimals={false} axisLine={false} tickLine={false} />
                                    <RechartsTooltip
                                        cursor={{ fill: palette.action.hover }}
                                        contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 8, boxShadow: muiTheme.shadows[8] }}
                                        itemStyle={{ color: palette.text.primary }}
                                    />
                                    {/* Per-stage coloring: Notified=blue, Approved=green, Not yet revoked=amber, Reminders=orange */}
                                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                        {approvalWorkflowChart.map((entry) => (
                                            <Cell key={entry.name} fill={
                                                entry.name === 'Notified' ? palette.primary.main
                                                    : entry.name === 'Approved' ? palette.success.main
                                                    : entry.name === 'Not yet revoked' ? palette.warning.light
                                                    : entry.name === 'Reminders' ? palette.warning.main
                                                    : palette.grey[400]
                                            } />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </DashboardCard>
                    </Box>

                    {/* Merged into one chart per mockup: the mockup only ever had a single
                        "acl_qoq" panel with both a Privileged-users and a Revocations line, on
                        a quarterly x-axis. The separate daily-granularity "Revocations Over
                        Time" panel (pre-existing, using revocation_date) is intentionally
                        removed rather than kept alongside this — the mockup has no equivalent
                        second panel, and revocations are already the second line here. Backend
                        sends quarterOverQuarter as a distinct pre-aggregated array (see
                        getAclRecords) since no per-record field could answer "what did this
                        look like last quarter" the way a raw filterable record can. */}
                    <Box sx={{ mb: 3 }}>
                        <DashboardCard title="Privileged Users — Quarter over Quarter">
                            {quarterOverQuarter.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                                    No historical quarters recorded yet.
                                </Typography>
                            ) : (
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={quarterOverQuarter} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={palette.divider} />
                                        <XAxis dataKey="period" tick={{ fontSize: 11, fill: palette.text.secondary }} />
                                        <YAxis tick={{ fontSize: 11, fill: palette.text.secondary }} allowDecimals={false} />
                                        <RechartsTooltip contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}` }} />
                                        <RechartsLegend wrapperStyle={{ fontSize: 11 }} />
                                        <Line type="monotone" dataKey="privileged_users" name="Privileged users" stroke={palette.error.main} strokeWidth={2} dot />
                                        <Line type="monotone" dataKey="revocations" name="Revocations" stroke={palette.primary.main} strokeWidth={2} dot />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </DashboardCard>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ color: 'text.secondary', letterSpacing: '0.05em' }}>Detailed Records</Typography>
                        <Button size="small" variant="outlined" onClick={() => downloadCsv(filtered, 'acl_data.csv')}>Download CSV</Button>
                    </Box>
                    {/* overflow: 'auto' overrides getTableContainerStyles()'s 'hidden' — otherwise
                        maxHeight clips long cell content instead of making it scrollable within
                        the table (ref: Detailed Records scroller fix). */}
                    <TableContainer component={Paper} sx={{ ...getTableContainerStyles(), boxShadow: 2, maxHeight: 420, overflow: 'auto' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {['Report', 'Username', 'Class', 'Group/Role', 'Ticket', 'Access Status', 'Access End', 'Revocation Date', 'Approval', 'Reminders', 'Revoke Access', 'Retain Access'].map((h) => (
                                        <TableCell key={h} sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles() }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtered.map((r, idx) => {
                                    const statusKey = (r.access_status || '').toLowerCase();
                                    // W-2: Fall back to a neutral tint for any status value not in ROW_COLOR.
                                    // M-3: Composite key prevents collision when rows share the same ticket value
                                    // (e.g. two users with identical ticket IDs). idx is a stable tiebreaker
                                    // within the current filtered view.
                                    return (
                                        <TableRow key={`${r.report_name ?? ''}:${r.username ?? ''}:${r.ticket ?? ''}:${idx}`} hover sx={{ ...getTableRowStyles(), bgcolor: ROW_COLOR[statusKey] ?? UNKNOWN_ROW_COLOR }}>
                                            <TableCell sx={getTableCellStyles()}>{r.report_name || '—'}</TableCell>
                                            <TableCell sx={getTableCellStyles()}>{r.username || '—'}</TableCell>
                                            <TableCell sx={getTableCellStyles()}>
                                                {isPrivileged(r) ? 'Privileged' : 'Non-privileged'}
                                            </TableCell>
                                            <TableCell sx={getTableCellStyles()}>
                                                {(isPrivileged(r) ? r.group_name : r.role) || '—'}
                                            </TableCell>
                                            <TableCell sx={getTableCellStyles()}>{r.ticket || '—'}</TableCell>
                                            <TableCell sx={getTableCellStyles()}>
                                                {r.access_status ? r.access_status.charAt(0).toUpperCase() + r.access_status.slice(1) : '—'}
                                            </TableCell>
                                            <TableCell sx={getTableCellStyles()}>{r.access_end || '—'}</TableCell>
                                            <TableCell sx={getTableCellStyles()}>{r.revocation_date || '—'}</TableCell>
                                            <TableCell sx={getTableCellStyles()}>
                                                {r.manager_decision || 'Awaiting'}
                                            </TableCell>
                                            <TableCell sx={getTableCellStyles()}>
                                                {r.reminders_sent ? `${r.reminders_sent} sent` : '—'}
                                            </TableCell>
                                            <TableCell sx={getTableCellStyles()}>
                                                {(() => {
                                                    // idx is omitted from this key intentionally: isAlreadyRevoked/isRetained
                                                    // (derived from persisted record fields) are the durable signal; this key
                                                    // only drives the loading spinner and optimistic error display.
                                                    const key = `${r.report_name ?? ''}:${r.username ?? ''}:${r.ticket ?? ''}`;
                                                    // access_status 'false' means access is already revoked — disable both action buttons.
                                                    const isAlreadyRevoked = (r.access_status || '').toLowerCase() === 'false';
                                                    if (revokeDone[key] || isAlreadyRevoked) {
                                                        return <Button variant="outlined" size="small" disabled sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}>Revoked</Button>;
                                                    }
                                                    return (
                                                        <>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                disabled={revokeLoading[key]}
                                                                onClick={() => requestRevokeAccess(r, key)}
                                                                sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}
                                                            >
                                                                {revokeLoading[key] ? 'Revoking...' : 'Revoke Access'}
                                                            </Button>
                                                            {revokeError[key] && (
                                                                <Box sx={{ fontSize: '0.65rem', color: 'error.main', mt: 0.5 }}>{revokeError[key]}</Box>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell sx={getTableCellStyles()}>
                                                {(() => {
                                                    const key = `${r.report_name ?? ''}:${r.username ?? ''}:${r.ticket ?? ''}`;
                                                    const isAlreadyRevoked = (r.access_status || '').toLowerCase() === 'false';
                                                    const isRetained = (r.manager_decision || '').toLowerCase() === 'approved';
                                                    if (retainDone[key] || isRetained) {
                                                        return <Button variant="outlined" size="small" disabled sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'success.main' }}>Retained</Button>;
                                                    }
                                                    return (
                                                        <>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                disabled={retainLoading[key] || isAlreadyRevoked}
                                                                onClick={() => handleRetainAccess(r, key)}
                                                                sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}
                                                            >
                                                                {retainLoading[key] ? 'Retaining...' : 'Retain Access'}
                                                            </Button>
                                                            {retainError[key] && (
                                                                <Box sx={{ fontSize: '0.65rem', color: 'error.main', mt: 0.5 }}>{retainError[key]}</Box>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
            <ConfirmActionDialog
                open={!!revokeConfirmTarget}
                title="Revoke Access"
                message={
                    <>
                        Are you sure you want to revoke access for{' '}
                        <strong>{revokeConfirmTarget?.r.username || 'this user'}</strong>?
                    </>
                }
                caveat="This action will remove their access and cannot be undone."
                confirmLabel="Revoke Access"
                confirmingLabel="Revoking..."
                confirming={!!revokeConfirmTarget && !!revokeLoading[revokeConfirmTarget.key]}
                onConfirm={handleRevokeAccess}
                onClose={() => setRevokeConfirmTarget(null)}
            />
        </Box>
    );
};

export default AclAccessControlDashboard;
